import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Callout, CopyButton, DialogFrame } from '@nyce/admin-kit/console';
import { Button, Switch } from '@nyce/admin-kit/ui';
import type { DbServerRef } from '../../api/db';
import { Card, EmptyState, ErrorBox, Loading, TableScroller, useAsync } from '../../components/common';
import { formatBytes, formatCount } from '../../lib/format';
import { DatabasePicker, useServerDatabases } from '../../pages/databases/shared';
import {
  ADVISOR_PATH,
  indexAdvisor,
  type AdviceRecommendation,
  type Coverage,
  type ExistingIndex,
  type TableStats,
} from '../api/indexAdvisor';
import {
  CoverageChip,
  ExistingIndexes,
  ExpandButton,
  saveSql,
  Statements,
  TableStatsView,
  useOpenRows,
} from '../shared';
import '../indexadvisor.css';

const UNPARSED = '(not parsed)';

/** New first: what the default set lacks matters most. */
const COVERAGE_ORDER: Record<Coverage, number> = {
  new: 0,
  extends: 1,
  unparsed: 2,
  covered: 3,
  in_default: 4,
};

/**
 * One database's index advice (Databases → Index advice): the advisor's recommendations per table with the statements
 * behind them, the table's size and write rate, its existing indexes and how each recommendation relates to the
 * default set; the database's `CREATE INDEX CONCURRENTLY` script.
 */
export function AdviceTab({
  server,
  database,
  onDatabase,
}: {
  server: DbServerRef;
  database: string;
  onDatabase: (db: string) => void;
}) {
  const dbs = useServerDatabases(server.id);
  const advice = useAsync(
    async () => (database ? indexAdvisor.advice(server.id, database) : null),
    [server.id, database]
  );
  const [script, setScript] = useState(false);
  const rows = useMemo(() => advice.data?.recommendations ?? [], [advice.data]);
  const groups = useMemo(() => {
    const by = new Map<string, AdviceRecommendation[]>();
    for (const r of rows) {
      const t = r.table ?? UNPARSED;
      by.set(t, [...(by.get(t) ?? []), r]);
    }
    return [...by.entries()]
      .sort(([a], [b]) => (a === UNPARSED ? 1 : b === UNPARSED ? -1 : a.localeCompare(b)))
      .map(([table, list]) => ({
        table,
        list: [...list].sort(
          (x, y) =>
            COVERAGE_ORDER[x.coverage] - COVERAGE_ORDER[y.coverage] ||
            (x.ddl ?? '').localeCompare(y.ddl ?? '')
        ),
      }));
  }, [rows]);
  const a = advice.data;
  return (
    <>
      <div className="iax-toolbar">
        <DatabasePicker databases={dbs.names} value={database} onChange={onDatabase} />
        {a && (
          <span className="iax-muted">
            {formatCount(rows.length)} recommendations on {formatCount(groups.length)} tables
          </span>
        )}
        <span className="iax-spacer" />
        {database && (
          <Button size="sm" variant="ghost" onClick={() => setScript(true)}>
            Script
          </Button>
        )}
        <Link to={ADVISOR_PATH} className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm">
          All databases
        </Link>
      </div>
      <ErrorBox error={dbs.error} />
      <ErrorBox error={advice.error} />
      {!database ? (
        <EmptyState
          card
          title="Pick a database"
          hint="Shows the indexes the database's index advisor recommends, with the statements behind them."
        />
      ) : (
        <>
          <Loading loading={advice.loading && !a} />
          {a?.defaultSetError && (
            <Callout tone="warning" title="The default set could not be read">
              {a.defaultSetError} — coverage is only checked against what could be read.
            </Callout>
          )}
          {a && !a.tenant && (
            <p className="nyk-ui-hint">
              Not a tenant database (no <code>tbe_client</code> / <code>tbe_warehouse</code>): fleet scans
              skip it.
            </p>
          )}
          {a?.status === 'no_advisor' && (
            <EmptyState
              card
              title="No index advisor in this database"
              hint={a.note ?? 'The advisor extension or its views are not available here.'}
            />
          )}
          {a?.status === 'ok' && !rows.length && (
            <EmptyState
              card
              title="No recommendations"
              hint={a.note ?? 'The advisor recommends no indexes here.'}
            />
          )}
          {a?.status === 'ok' && a.note && !!rows.length && <p className="nyk-ui-hint">{a.note}</p>}
          {!!groups.length && a && (
            <div className="iax-tables">
              {groups.map((g) => (
                <TableCard
                  key={g.table}
                  table={g.table}
                  list={g.list}
                  stats={a.tables[g.table]}
                  indexes={a.indexes[g.table]}
                />
              ))}
            </div>
          )}
        </>
      )}
      {script && database && (
        <ScriptDialog server={server} database={database} onClose={() => setScript(false)} />
      )}
    </>
  );
}

function TableCard({
  table,
  list,
  stats,
  indexes,
}: {
  table: string;
  list: AdviceRecommendation[];
  stats?: TableStats;
  indexes?: ExistingIndex[];
}) {
  const rows = useOpenRows();
  const [showIndexes, setShowIndexes] = useState(false);
  const indexesId = `iax-idx-${table}`;
  return (
    <Card
      title={table}
      hint={
        stats ? (
          <span className="iax-table-stats">
            <span>{formatCount(stats.rows)} rows</span>
            <span>{formatBytes(stats.totalBytes)}</span>
            <span>{formatCount(stats.inserts + stats.updates + stats.deletes)} writes</span>
            <span>
              {formatCount(stats.seqScans)} seq / {formatCount(stats.idxScans)} index scans
            </span>
          </span>
        ) : undefined
      }
      actions={
        indexes ? (
          <ExpandButton open={showIndexes} onToggle={() => setShowIndexes((v) => !v)} controls={indexesId}>
            {`Indexes (${indexes.length})`}
          </ExpandButton>
        ) : undefined
      }
    >
      <div data-table={table}>
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" aria-label={`Recommended indexes on ${table}`}>
            <thead>
              <tr>
                <th>Recommended index</th>
                <th>Default set</th>
                <th className="nyk-ui-num">Statements</th>
                <th />
              </tr>
            </thead>
            {list.map((r) => {
              const open = rows.isOpen(r.id);
              return (
                <tbody key={r.id} className="nyk-row-group" data-rec={r.id}>
                  <tr>
                    <td className="nyk-card-title">
                      <span className="iax-ddl">{r.ddl ?? r.recommended}</span>
                    </td>
                    <td data-label="Default set">
                      <CoverageChip coverage={r.coverage} coveredBy={r.coveredBy} />
                    </td>
                    <td data-label="Statements" className="nyk-ui-num">
                      {formatCount(r.statements.length)}
                    </td>
                    <td className="nyk-row-actions">
                      <ExpandButton open={open} onToggle={() => rows.toggle(r.id)}>
                        Statements
                      </ExpandButton>
                    </td>
                  </tr>
                  {open && (
                    <tr className="iax-detail-row">
                      <td colSpan={4}>
                        <div className="iax-detail">
                          {r.ddl && r.ddl !== r.recommended && (
                            <p className="nyk-ui-hint">
                              Advisor: <span className="iax-ddl">{r.recommended}</span>
                            </p>
                          )}
                          <Statements statements={r.statements} />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </TableScroller>
        {stats && showIndexes && (
          <div className="iax-detail" id={indexesId}>
            <TableStatsView stats={stats} />
          </div>
        )}
        {indexes && showIndexes && (
          <div className="iax-detail" id={stats ? undefined : indexesId}>
            <TableScroller>
              <ExistingIndexes indexes={indexes} label={`Existing indexes on ${table}`} />
            </TableScroller>
          </div>
        )}
      </div>
    </Card>
  );
}

/** The database's script: the default set's additions it lacks, optionally plus the latest scan's proposals. */
function ScriptDialog({
  server,
  database,
  onClose,
}: {
  server: DbServerRef;
  database: string;
  onClose: () => void;
}) {
  const [withScan, setWithScan] = useState(false);
  const [conditional, setConditional] = useState(false);
  const text = useAsync(
    () =>
      indexAdvisor.script(server.id, database, {
        scanId: withScan ? 'latest' : undefined,
        conditional,
      }),
    [server.id, database, withScan, conditional]
  );
  const name = `indexes-${server.id}-${database}.sql`;
  return (
    <DialogFrame
      title={`Index script for ${database}`}
      size="lg"
      onClose={onClose}
      dirty={false}
      footer={
        <>
          {text.data != null && <CopyButton text={text.data} label="Copy" />}
          <Button
            variant="secondary"
            disabled={text.data == null}
            onClick={() => text.data != null && saveSql(text.data, name)}
          >
            Download
          </Button>
        </>
      }
    >
      <p className="nyk-ui-hint">
        <code>CREATE INDEX CONCURRENTLY</code> for what this database lacks. The hub never runs it: review and
        apply it yourself.
      </p>
      <div className="iax-toolbar">
        <Switch checked={withScan} onChange={setWithScan} label="Add the latest scan's proposals" />
        <Switch checked={conditional} onChange={setConditional} label="Include conditional indexes" />
      </div>
      <ErrorBox error={text.error} />
      <Loading loading={text.loading && text.data == null} />
      {text.data != null && (
        <pre className="iax-sql-block" data-testid="iax-script">
          {text.data}
        </pre>
      )}
    </DialogFrame>
  );
}
